import type { CSSProperties } from 'react';
// Pure view logic for the Roadmap v2 page (2026-08-31 check-in asks): team
// colours, team/person/status/health filtering, view-by grouping, sorting,
// blocked-by pairs and run-grouped history. Kept out of the components so
// every rule is unit-testable without rendering.

import type {
  ActionStatus,
  ClarifyingQuestion,
  ClarifyingQuestionsResponse,
  GroupQuestion,
  InitiativeAction,
  InitiativeState,
  InitiativeStatus,
  IntegrityGap,
  RoadmapEvent,
  ScheduleHealth,
  TeamMandate,
} from '../../services/roadmapReports';

// ── Teams & colours ──────────────────────────────────────────────────────────

// Scott's example was "blue-and-orange striped" for an AI + Delta-team item, so
// the well-known task forces get fixed hues; anything else takes the next
// palette entry in sorted-name order (stable across renders and runs).
const TEAM_HINTS: Array<[RegExp, string]> = [
  [/\bai\b|merlin/i, '#2563eb'], // blue
  [/delta/i, '#f97316'], // orange
  [/alpha/i, '#16a34a'], // green
];
const PALETTE = ['#9333ea', '#0891b2', '#db2777', '#ca8a04', '#4f46e5', '#0d9488', '#dc2626', '#65a30d'];
export const NO_TEAM = 'No team';
export const NO_TEAM_COLOR = '#94a3b8';

export type TeamColors = Record<string, string>;

/** Anything that carries the v9 team fields next to the flat `teams` list. */
export interface Teamed {
  teams: string[];
  owner_team?: string | null;
  related_teams?: string[];
}

/** v9: the ONE owning team. A row the API has not resolved yet (no
 * owner_team) follows the backend's fold: a single team owns the item,
 * several teams mean nobody has been named. */
export function ownerTeamOf(i: Teamed): string | null {
  if (i.owner_team !== undefined) return i.owner_team;
  return i.teams.length === 1 ? i.teams[0] : null;
}

/** v9: teams with a stake that do not own the item (never the owner). */
export function relatedTeamsOf(i: Teamed): string[] {
  if (i.related_teams !== undefined) return i.related_teams;
  return i.teams.length === 1 ? [] : i.teams;
}

/** The team an initiative is painted / laned / grouped under: its owner, or,
 * when nobody has been named, the first non-AI team it carries (the task
 * force does the work; AI assists), else its first team, else null. */
export function accentTeamOf(i: Teamed): string | null {
  return ownerTeamOf(i) ?? i.teams.find((t) => t !== 'AI') ?? i.teams[0] ?? null;
}

/** The team buckets an initiative is listed under on a by-team view: ONLY its
 * owner once one is named (a related team's view does not claim it); every
 * team it carries while nobody is named; the no-team bucket otherwise. */
export function groupTeamsOf(i: Teamed): string[] {
  const owner = ownerTeamOf(i);
  if (owner) return [owner];
  return i.teams.length > 0 ? i.teams : [NO_TEAM];
}

/** "Alpha team · related: AI" — the owner first, the rest marked as related;
 * "no owner yet · AI, Delta team" while nobody has been named. */
export function teamsLine(i: Teamed): string {
  const owner = ownerTeamOf(i);
  const related = relatedTeamsOf(i);
  if (!owner) return related.length > 0 ? `no owner yet · ${related.join(', ')}` : '';
  return related.length > 0 ? `${owner} · related: ${related.join(', ')}` : owner;
}

/** The row's team line under a group heading (kanban 778cc5a0): grouped by
 * team or by mandate the heading already names the owner, so an owned row
 * says only who else has a stake ("related: AI") or nothing at all; a row
 * nobody owns still says so. Ungrouped and by-person rows read the full line. */
export function rowTeamsLine(i: Teamed, viewBy: ViewBy): string {
  if ((viewBy === 'team' || viewBy === 'mandate') && ownerTeamOf(i)) {
    const related = relatedTeamsOf(i);
    return related.length > 0 ? `related: ${related.join(', ')}` : '';
  }
  return teamsLine(i);
}

// ── Mandate areas (v10, kanban 838512f8) ─────────────────────────────────────

/** The reserved area key: the team's own work, outside every area it has. */
export const OTHER_AREA = 'other';
export const OTHER_AREA_TITLE = 'Outside its mandate';
export const UNFILED_AREA_TITLE = 'Not filed under a mandate area';

export interface MandateAreaRef { key: string; title: string }
/** `{folded team name: its areas, in the team's order}`. A team with an empty
 * list is registered with no mandate yet; a team absent has none on record. */
export type MandateLookup = Record<string, MandateAreaRef[]>;

export const foldTeam = (team: string): string => team.split(/\s+/).filter(Boolean).join(' ').toLowerCase();

export function mandateLookup(teams: readonly TeamMandate[]): MandateLookup {
  const out: MandateLookup = {};
  for (const t of teams) out[foldTeam(t.team)] = t.areas.map((a) => ({ key: a.key, title: a.title }));
  return out;
}

/** The areas of an initiative's owning team, or null when the team has no
 * mandate on record (so there is nothing to file it under). */
export function mandateAreasOf(i: Teamed, lookup: MandateLookup): MandateAreaRef[] | null {
  const owner = ownerTeamOf(i);
  if (!owner) return null;
  const areas = lookup[foldTeam(owner)];
  return areas && areas.length > 0 ? areas : null;
}

/** The human title of the area an initiative is filed under: the API's title
 * when it sent one, else the registry's, "Outside its mandate" for the
 * reserved key; null while unfiled. */
export function mandateAreaTitle(
  i: Teamed & { mandate_area?: string | null; mandate_area_title?: string | null },
  lookup: MandateLookup = {},
): string | null {
  const key = i.mandate_area ?? null;
  if (!key) return null;
  if (key === OTHER_AREA) return OTHER_AREA_TITLE;
  if (i.mandate_area_title) return i.mandate_area_title;
  const owner = ownerTeamOf(i);
  const area = owner ? (lookup[foldTeam(owner)] ?? []).find((a) => a.key === key) : undefined;
  return area ? area.title : key;
}

/** Distinct team names across the tracked set, sorted for stable chips. */
export function distinctTeams(initiatives: InitiativeState[]): string[] {
  const seen = new Map<string, string>();
  for (const i of initiatives) {
    for (const t of i.teams) {
      const k = t.toLowerCase();
      if (!seen.has(k)) seen.set(k, t);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** One colour per team; hinted names first, then the palette in order. */
export function assignTeamColors(teams: string[]): TeamColors {
  const colors: TeamColors = {};
  const used = new Set<string>();
  for (const t of teams) {
    const hint = TEAM_HINTS.find(([re]) => re.test(t));
    if (hint && !used.has(hint[1])) {
      colors[t] = hint[1];
      used.add(hint[1]);
    }
  }
  let next = 0;
  for (const t of teams) {
    if (colors[t]) continue;
    while (next < PALETTE.length && used.has(PALETTE[next])) next += 1;
    const c = PALETTE[next % PALETTE.length];
    colors[t] = c;
    used.add(c);
    next += 1;
  }
  return colors;
}

/** Colours for one initiative's teams (empty -> the "no team" grey). */
export function colorsFor(i: InitiativeState, colors: TeamColors): string[] {
  if (i.teams.length === 0) return [NO_TEAM_COLOR];
  return i.teams.map((t) => colors[t] ?? NO_TEAM_COLOR);
}

/** Cross-team: other teams have a stake (related, or 2+ teams with no owner
 * named), or linked (depends_on / related_to / blocks) to an initiative on a
 * different team. */
export function isCrossTeam(i: InitiativeState, byKey: Map<string, InitiativeState>): boolean {
  if (relatedTeamsOf(i).length > 0) return true;
  const mine = new Set(i.teams.map((t) => t.toLowerCase()));
  if (mine.size === 0) return false;
  for (const k of [...i.depends_on, ...i.related_to, ...i.blocks]) {
    const other = byKey.get(k);
    if (other && other.teams.some((t) => !mine.has(t.toLowerCase()))) return true;
  }
  return false;
}

// ── People ───────────────────────────────────────────────────────────────────

export const UNASSIGNED = 'Unassigned';

export function distinctPeople(initiatives: InitiativeState[]): string[] {
  const seen = new Map<string, string>();
  for (const i of initiatives) {
    for (const p of i.people) {
      const k = p.toLowerCase();
      if (!seen.has(k)) seen.set(k, p);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

// ── Health & status labels ───────────────────────────────────────────────────

export const HEALTH_LABELS: Record<ScheduleHealth, string> = {
  on_track: 'On track',
  late: 'Late',
  at_risk: 'At risk',
  stuck: 'Stuck',
  no_target: 'No target',
  parked: 'Parked',
  done: 'Done',
  dropped: 'Dropped',
};

export const STATUS_LABELS: Record<InitiativeStatus, string> = {
  proposed: 'Proposed',
  in_progress: 'In progress',
  blocked: 'Blocked',
  waiting: 'Waiting',
  ongoing: 'Ongoing',
  parked: 'Parked',
  done: 'Done',
  dropped: 'Dropped',
  unknown: 'Unknown',
};

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
  dropped: 'Dropped',
  unknown: 'Unknown',
};

// ── Sub-actions (contract v3) ────────────────────────────────────────────────

/** Actions still worth a row: dropped ones are history, not plan. */
export function liveActions(actions: InitiativeAction[]): InitiativeAction[] {
  return actions.filter((a) => a.status !== 'dropped');
}

/** The dates an action row shows: "start → end" with each side a stated
 * date or an honest blank ("?"). A done action's end is its completion date. */
export function actionDateSpan(a: InitiativeAction): { start: string | null; end: string | null; endKind: 'completed' | 'target' | null } {
  const end = a.status === 'done' && a.completed_on ? a.completed_on : a.target_date;
  const endKind = end ? (a.status === 'done' && a.completed_on ? 'completed' : 'target') : null;
  return { start: a.started_on, end, endKind };
}

/** A sibling sub-action named from another: its key (to open it) and its title (to read). */
export interface ActionRef {
  key: string;
  title: string;
}

/** The sibling actions an action waits on, by key and title (a key with no
 * live sibling reads as the key itself, so a dangling dependency still shows). */
export function actionBlockerRefs(a: InitiativeAction, siblings: InitiativeAction[]): ActionRef[] {
  const byKey = new Map(siblings.map((s) => [s.key, s.title]));
  return a.depends_on.map((k) => ({ key: k, title: byKey.get(k) ?? k }));
}

/** The sibling actions that wait on `a` (the mirror of actionBlockerRefs). */
export function actionHoldsUpRefs(a: InitiativeAction, siblings: InitiativeAction[]): ActionRef[] {
  return siblings.filter((s) => s.key !== a.key && s.depends_on.includes(a.key)).map((s) => ({ key: s.key, title: s.title }));
}

/** Titles of the sibling actions an action waits on (keys → titles). */
export function actionBlockers(a: InitiativeAction, siblings: InitiativeAction[]): string[] {
  return actionBlockerRefs(a, siblings).map((r) => r.title);
}

/** Titles of the sibling actions that wait on `a` (the mirror of actionBlockers). */
export function actionHoldsUp(a: InitiativeAction, siblings: InitiativeAction[]): string[] {
  return actionHoldsUpRefs(a, siblings).map((r) => r.title);
}

const foldTitle = (t: string) => t.trim().replace(/(…|\.\.\.)$/, '').trim().toLowerCase();

/**
 * The key of the sub-action a title names on an initiative, so a place that
 * only knows the title (the week overview's "next step" and "done since"
 * lines, which come from a stored report) can still open the sub-action
 * pop-up. Exact title first, then case-folded with a trailing clip mark
 * ignored; null when nothing on the initiative carries that title.
 */
export function resolveActionKey(i: Pick<InitiativeState, 'actions'> | null | undefined, title: string | null | undefined): string | null {
  if (!i || !title) return null;
  const exact = i.actions.find((a) => a.title === title);
  if (exact) return exact.key;
  const want = foldTitle(title);
  if (!want) return null;
  return i.actions.find((a) => foldTitle(a.title) === want)?.key ?? null;
}

/** A sub-action is late when its stated end has passed and it is neither done nor dropped. */
export function actionIsLate(a: InitiativeAction, today: string): boolean {
  const { end } = actionDateSpan(a);
  return end !== null && end < today && a.status !== 'done' && a.status !== 'dropped';
}

/** "2 of 3 done" style summary for a collapsed row; null when no actions. */
export function actionsSummary(actions: InitiativeAction[]): string | null {
  const live = liveActions(actions);
  if (live.length === 0) return null;
  const done = live.filter((a) => a.status === 'done').length;
  return done > 0 ? `${done} of ${live.length} done` : `${live.length} action${live.length === 1 ? '' : 's'}`;
}

/** The chip every list row carries (2026-09-27 assessment: "1 of 3 done"
 * existed on some rows only): the summary when there are live sub-actions,
 * an explicit "no sub-actions yet" otherwise so the absence reads as a fact
 * rather than a missing chip. */
export const NO_ACTIONS_LABEL = 'no sub-actions yet';
export function actionsChip(actions: InitiativeAction[]): { label: string; empty: boolean } {
  const summary = actionsSummary(actions);
  return summary ? { label: summary, empty: false } : { label: NO_ACTIONS_LABEL, empty: true };
}

// ── Decide-by dates in questions (PJ, 2026-09-08: push for dates) ───────────

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * The proposed decide-by date carried in a question's text, as an ISO date,
 * or null when the question is open-ended. Merlin is prompted to end each
 * question with "Decide by YYYY-MM-DD"; a spoken form ("by Sep 15",
 * "by September 15, 2026") is read too. The year of a month-day form defaults
 * to the report's year (or the current one) — the chip is a nudge, not a claim.
 */
export function decideByFromQuestion(question: string, fallbackYear = new Date().getFullYear()): string | null {
  const iso = /\b(20\d{2})-(\d{2})-(\d{2})\b/.exec(question);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const spoken = /\bby\s+(jan|feb|mar|apr|may|jun|jul|aug|sept?|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(20\d{2}))?/i.exec(question);
  if (!spoken) return null;
  const month = MONTHS[spoken[1].toLowerCase()];
  const day = Number(spoken[2]);
  if (!month || day < 1 || day > 31) return null;
  const year = spoken[3] ? Number(spoken[3]) : fallbackYear;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The question with its trailing "Decide by …" clause removed, for display
 * next to the date chip (the sentence is not repeated twice). */
export function questionWithoutDecideBy(question: string): string {
  return question.replace(/\s*[—–-]?\s*(let'?s\s+)?(revisit\s+and\s+)?decide\s+by\s+[^.;]*[.;]?\s*$/i, '').trim() || question;
}

/** How a decide-by date sits against today: past, this week, or later. */
export function decideByUrgency(iso: string, today: string): 'overdue' | 'soon' | 'later' {
  if (iso < today) return 'overdue';
  const t = new Date(`${today}T12:00:00Z`).getTime();
  const d = new Date(`${iso}T12:00:00Z`).getTime();
  return d - t <= 7 * 24 * 60 * 60 * 1000 ? 'soon' : 'later';
}

export type DecideByState = 'overdue' | 'soon' | 'later' | 'awaiting';

/** The pill state once the report that asked is taken into account.
 *
 * Merlin sets every decide-by date for the NEXT meeting, and only a run
 * resets it, so between runs a passed date is the normal state, not a
 * failure (2026-09-27: 52 of 52 cards read "decide-by date passed"). A date
 * that passed AFTER the report was written is "awaiting" (asked on the
 * report date, for the next meeting); it is "overdue" only when it was
 * already in the past when the report was written, i.e. a run carried the
 * question over with its date unanswered. */
export function decideByState(iso: string, today: string, reportDate: string | null): DecideByState {
  const urgency = decideByUrgency(iso, today);
  if (urgency !== 'overdue') return urgency;
  if (reportDate && iso >= reportDate) return 'awaiting';
  return 'overdue';
}

/** The person a question card is filed under: the addressee Merlin named,
 * else the initiative's first person, else Unassigned. */
export function questionAddressee(q: Pick<ClarifyingQuestion, 'addressed_to' | 'people'>): string {
  return q.addressed_to?.[0] ?? q.people[0] ?? UNASSIGNED;
}

// The gap kinds whose answer unblocks the roadmap today (owner, date, blocker);
// the other kinds (stale, finished blocker, no team) are Merlin's to fix.
const NEEDS_ANSWER_KINDS = new Set(['no_owner', 'no_target', 'blocked_without_blocker', 'parked_without_reason']);
const GAP_QUESTION_HINTS: Record<string, RegExp> = {
  no_owner: /\bown(s|er|ership)?\b|\bwho (formally )?(runs|leads|drives)\b/i,
  no_target: /\b(date|when|deadline|target|timeline|eta|by (end|q[1-4]|mid|late|early)|schedul)/i,
  blocked_without_blocker: /\bblock|\bwaiting\b|\bdepend|\bhold(ing|s)? up\b/i,
  parked_without_reason: /\bpark|\bon hold\b|\bsidelin|\bback burner\b|\btabled?\b|\bwhy\b/i,
};

/** Everyone a question card is listed under: every addressee Merlin named
 * ("Hadi Tawil / Paul J Murphy" is both people's question, not only the
 * first one's), else the single fallback from questionAddressee. */
export function questionAddressees(q: Pick<ClarifyingQuestion, 'addressed_to' | 'people'>): string[] {
  return q.addressed_to && q.addressed_to.length > 0 ? [...new Set(q.addressed_to)] : [questionAddressee(q)];
}

/**
 * The question without the "Hadi Tawil / Kevin:" lead-in, for places that
 * already show who it is for (a person's own list, the meeting view's name
 * tag). Only a lead-in made of the question's own addressees is removed.
 */
export function questionBody(q: Pick<ClarifyingQuestion, 'question' | 'addressed_to'>): string {
  const text = questionWithoutDecideBy(q.question);
  const m = /^([^:]{2,90}):\s+(.+)$/s.exec(text);
  if (!m || !q.addressed_to || q.addressed_to.length === 0) return text;
  // Every part of the lead-in has to BE an addressee (full name or first
  // name, with an optional "(note)"): "The gravity feed line: ..." addressed
  // to Ravi is a sentence, not a lead-in, and stays whole.
  const names = new Set(q.addressed_to.flatMap((p) => [p.toLowerCase(), p.split(' ')[0].toLowerCase()]));
  const parts = m[1].split(/\s*\/\s*|\s*,\s*|\s+and\s+/i).map((p) => p.replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase());
  if (parts.length === 0 || !parts.every((p) => names.has(p))) return text;
  return m[2].charAt(0).toUpperCase() + m[2].slice(1);
}

export interface NeedsAnswerItem {
  gapKind: string;
  question: ClarifyingQuestion;
}

/** The questions the group must answer today: for each integrity gap of an
 * answerable kind, the initiative's question that asks about that gap (by
 * wording), else its first open question. One card per question. */
export function needsAnswerNow(items: ClarifyingQuestion[], gaps: IntegrityGap[]): NeedsAnswerItem[] {
  const byKey = new Map<string, ClarifyingQuestion[]>();
  for (const q of items) byKey.set(q.initiative_key, [...(byKey.get(q.initiative_key) ?? []), q]);
  const taken = new Set<string>();
  const out: NeedsAnswerItem[] = [];
  for (const g of gaps) {
    if (!NEEDS_ANSWER_KINDS.has(g.kind)) continue;
    const qs = byKey.get(g.initiative_key) ?? [];
    if (qs.length === 0) continue;
    const hint = GAP_QUESTION_HINTS[g.kind];
    const pick = qs.find((q) => hint.test(q.question) && !taken.has(`${q.initiative_key}\u0000${q.question}`))
      ?? qs.find((q) => !taken.has(`${q.initiative_key}\u0000${q.question}`));
    if (!pick) continue;
    taken.add(`${pick.initiative_key}\u0000${pick.question}`);
    out.push({ gapKind: g.kind, question: pick });
  }
  return out;
}

// Sort weight: what needs the meeting's attention first.
const HEALTH_RANK: Record<ScheduleHealth, number> = {
  late: 0,
  at_risk: 1,
  stuck: 2,
  no_target: 3,
  on_track: 4,
  parked: 5,
  done: 6,
  dropped: 7,
};

/** Why an initiative carries its health — the rule stated with its own
 * dates, so the chip explains itself (trust-building tooltips, borrowed from
 * the Guru Cloud AI client portal). */
export function healthExplanation(i: InitiativeState, today: string, stuckAfterDays = 14): string {
  switch (i.health) {
    case 'late':
      return `Late: the stated target ${i.target_date} has passed (today ${today}) and it is not done.`;
    case 'at_risk':
      return `At risk: target ${i.target_date} cannot hold — something it waits on finishes later or has no date.`;
    case 'stuck':
      return `Stuck: no roadmap run has recorded any change since ${i.updated_at.slice(0, 10)} (${stuckAfterDays}+ days).`;
    case 'no_target':
      return 'No target: nobody has stated a completion date. Merlin asks for one rather than guessing.';
    case 'on_track':
      return `On track: target ${i.target_date} is ahead and it moved within the last ${stuckAfterDays} days.`;
    case 'parked':
      return 'Parked: sidelined or blocked with no end in sight, so it has no schedule. It sits in the Parked tab until someone picks it back up.';
    case 'done':
      return 'Done: the group said this is finished.';
    case 'dropped':
      return 'Dropped: the group said this is no longer pursued.';
    default:
      return '';
  }
}

export const GAP_KIND_LABELS: Record<string, string> = {
  at_risk: 'Dates that cannot hold',
  blocked_without_blocker: 'Blocked without a named blocker',
  dependency_in_prose: 'Dependency stated in prose only (no arrow yet)',
  no_target: 'No target date and nobody asked yet',
  no_owner: 'Nobody named',
  stale: 'No movement recorded',
  finished_blocker: 'Dependency already satisfied',
  no_team: 'No team tag',
  no_mandate_area: "Not filed under the owning team's mandate",
  parked_without_reason: 'Parked without a reason',
};

// ── Filtering / sorting / grouping ───────────────────────────────────────────

/** How the Initiatives tab is laid out: grouped by nothing / team / mandate /
 * person, or 'actions' — the sub-action view, one row per live sub-action
 * with all its details (owner, 2026-10-20: "a sub-action mode that shows all
 * the details"). */
export type ViewBy = 'list' | 'team' | 'mandate' | 'person' | 'actions';
export type SortKey = 'attention' | 'updated' | 'target' | 'title';

export interface RoadmapFilters {
  teams: string[]; // empty = all
  person: string | null; // null = all
  status: InitiativeStatus | 'all';
  health: ScheduleHealth | 'all';
  text: string;
  includeClosed: boolean; // done + dropped
  /** Only what names me or my teams (the portal's "Mine", owner ask 2026-10-15). */
  mine: boolean;
}

/** Who the reader is on the roadmap: the roster names that are them and the
 * task forces the people directory places them on (GET /api/my-roadmap). */
export interface RoadmapMe {
  names: string[];
  teams: string[];
}

const fold = (s: string) => s.trim().toLowerCase();

/** The reader is named on the initiative, or one of their teams owns it or has a stake in it. */
export function isMine(i: Pick<InitiativeState, 'people' | 'teams'>, me: RoadmapMe): boolean {
  const names = new Set(me.names.map(fold));
  const teams = new Set(me.teams.map(fold));
  return i.people.some((p) => names.has(fold(p))) || i.teams.some((t) => teams.has(fold(t)));
}

/** The question is addressed to the reader (the only ones they may answer). */
export function isAddressedToMe(q: Pick<ClarifyingQuestion, 'addressed_to' | 'people'>, me: RoadmapMe): boolean {
  const names = new Set(me.names.map(fold));
  const addressees = q.addressed_to && q.addressed_to.length > 0 ? q.addressed_to : q.people;
  return addressees.some((p) => names.has(fold(p)));
}

/** The question is the reader's: addressed to them, or on an initiative that is theirs. */
export function questionIsMine(q: Pick<ClarifyingQuestion, 'addressed_to' | 'people' | 'teams'>, me: RoadmapMe): boolean {
  return isAddressedToMe(q, me) || isMine(q, me);
}

/** Whether any narrowing filter is on (showing done / dropped is not a narrowing). */
export function anyFilterOn(f: RoadmapFilters): boolean {
  return f.teams.length > 0 || f.person !== null || f.text.trim() !== '' || f.status !== 'all' || f.health !== 'all' || f.mine;
}

/** Parked initiatives live in their own bucket (the Parked tab): every other
 * view hides them unless the status filter asks for them by name. */
export function isParked(i: Pick<InitiativeState, 'status'>): boolean {
  return i.status === 'parked';
}

export const DEFAULT_FILTERS: RoadmapFilters = {
  teams: [],
  person: null,
  status: 'all',
  health: 'all',
  text: '',
  includeClosed: false,
  mine: false,
};

function matchesText(i: InitiativeState, text: string): boolean {
  const q = text.trim().toLowerCase();
  if (!q) return true;
  return [i.title, i.summary, i.owner ?? '', ...i.people, ...i.teams, i.initiative_key]
    .join(' ')
    .toLowerCase()
    .includes(q);
}

export function applyFilters(
  initiatives: InitiativeState[],
  f: RoadmapFilters,
  me: RoadmapMe | null = null,
): InitiativeState[] {
  const teams = new Set(f.teams.map((t) => t.toLowerCase()));
  const person = f.person?.toLowerCase() ?? null;
  // "Mine" with no reader known (or one the roadmap does not name) keeps nothing:
  // an honest empty list, never a silent fallback to everything.
  const mine = f.mine ? me ?? { names: [], teams: [] } : null;
  return initiatives.filter((i) => {
    if (mine && !isMine(i, mine)) return false;
    if (!f.includeClosed && (i.status === 'done' || i.status === 'dropped')) return false;
    if (isParked(i) && f.status !== 'parked') return false;
    if (teams.size > 0 && !i.teams.some((t) => teams.has(t.toLowerCase()))) return false;
    if (person !== null) {
      const has = i.people.some((p) => p.toLowerCase() === person);
      if (person === UNASSIGNED.toLowerCase() ? i.people.length > 0 : !has) return false;
    }
    if (f.status !== 'all' && i.status !== f.status) return false;
    if (f.health !== 'all' && i.health !== f.health) return false;
    return matchesText(i, f.text);
  });
}

export function sortInitiatives(
  initiatives: InitiativeState[],
  key: SortKey,
): InitiativeState[] {
  const copy = [...initiatives];
  switch (key) {
    case 'attention':
      copy.sort(
        (a, b) =>
          HEALTH_RANK[a.health] - HEALTH_RANK[b.health] ||
          (a.target_date ?? '9999').localeCompare(b.target_date ?? '9999') ||
          a.title.localeCompare(b.title, undefined, { numeric: true }),
      );
      break;
    case 'target':
      // Dated first (soonest), undated last.
      copy.sort(
        (a, b) =>
          (a.target_date ?? '9999').localeCompare(b.target_date ?? '9999') ||
          a.title.localeCompare(b.title, undefined, { numeric: true }),
      );
      break;
    case 'title':
      copy.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
      break;
    case 'updated':
    default:
      copy.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }
  return copy;
}

export interface InitiativeGroup {
  label: string;
  color: string | null;
  items: InitiativeState[];
  /** By-mandate groups: the owning team and the area (null = unfiled / no mandate). */
  team?: string;
  area?: string | null;
}

/** Group for the view-by mode. An initiative tagged with several teams (or
 * several people) appears under EACH — that is the point of the view: one
 * person holding actions several teams are waiting on shows up everywhere it
 * matters. */
/** The single group the ungrouped list is returned as. */
export const ALL_INITIATIVES_LABEL = 'All initiatives';

export function groupInitiatives(
  initiatives: InitiativeState[],
  viewBy: ViewBy,
  colors: TeamColors,
  /** Teams the page is filtered to. A shared initiative is then grouped only
   * under those teams, so "AI only, by team" shows one AI group and not also
   * partial Alpha and Delta groups (kanban 0baf9993). */
  onlyTeams: string[] = [],
  /** The mandate registry, for the by-mandate view (PJ, 2026-10-12: "whatever
   * is under this button when I push it is going to have to do with
   * housekeeping on the Alpha team"). */
  mandates: MandateLookup = {},
): InitiativeGroup[] {
  // The sub-action view groups by initiative itself (SubActionsTable), so it takes the plain order.
  if (viewBy === 'list' || viewBy === 'actions') return [{ label: ALL_INITIATIVES_LABEL, color: null, items: initiatives }];
  const only = new Set(onlyTeams.map((t) => t.toLowerCase()));
  const groups = new Map<string, InitiativeGroup>();
  const add = (label: string, color: string | null, i: InitiativeState, extra: Pick<InitiativeGroup, 'team' | 'area'> = {}) => {
    const g = groups.get(label.toLowerCase()) ?? { label, color, items: [], ...extra };
    g.items.push(i);
    groups.set(label.toLowerCase(), g);
  };
  if (viewBy === 'mandate') {
    // One heading per (owning team, area), in the team's own order of areas,
    // then the team's work outside its mandate, then what is not filed yet.
    // A team with no mandate on record is one heading; ownerless items last.
    const rank = (g: InitiativeGroup): [string, number] => {
      if (g.label === NO_TEAM) return ['\uffff', 0];
      const areas = mandates[foldTeam(g.team ?? '')] ?? [];
      if (g.area === null || g.area === undefined) return [g.team ?? '', areas.length + 2];
      if (g.area === OTHER_AREA) return [g.team ?? '', areas.length + 1];
      const n = areas.findIndex((a) => a.key === g.area);
      return [g.team ?? '', n < 0 ? areas.length + 2 : n];
    };
    for (const i of initiatives) {
      const owner = ownerTeamOf(i);
      if (!owner) {
        add(NO_TEAM, NO_TEAM_COLOR, i, { area: null });
        continue;
      }
      if (only.size > 0 && !only.has(owner.toLowerCase())) continue;
      const color = colors[owner] ?? NO_TEAM_COLOR;
      const areas = mandateAreasOf(i, mandates);
      if (!areas) {
        add(owner, color, i, { team: owner, area: null });
        continue;
      }
      const title = mandateAreaTitle(i, mandates);
      const area = i.mandate_area ?? null;
      const known = area !== null && (area === OTHER_AREA || areas.some((a) => a.key === area));
      add(`${owner} · ${known ? title : UNFILED_AREA_TITLE}`, color, i, { team: owner, area: known ? area : null });
    }
    return [...groups.values()].sort((a, b) => {
      const [ta, na] = rank(a);
      const [tb, nb] = rank(b);
      return ta.localeCompare(tb) || na - nb;
    });
  }
  for (const i of initiatives) {
    if (viewBy === 'team') {
      // v9: an owned initiative is listed under its owner only — a team it is
      // merely related to does not get it in its group (owner ruling 2026-10-12).
      for (const t of groupTeamsOf(i)) {
        if (t === NO_TEAM) {
          add(NO_TEAM, NO_TEAM_COLOR, i);
          continue;
        }
        if (only.size > 0 && !only.has(t.toLowerCase())) continue;
        add(t, colors[t] ?? NO_TEAM_COLOR, i);
      }
    } else {
      if (i.people.length === 0) add(UNASSIGNED, null, i);
      for (const p of i.people) add(p, null, i);
    }
  }
  const ordered = [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  // The catch-all buckets go last so the real teams/people lead.
  const tail = new Set([NO_TEAM.toLowerCase(), UNASSIGNED.toLowerCase()]);
  return [
    ...ordered.filter((g) => !tail.has(g.label.toLowerCase())),
    ...ordered.filter((g) => tail.has(g.label.toLowerCase())),
  ];
}

/** Flatten groups back to a de-duplicated ordered list (the Gantt's rows). */
export function flattenGroups(groups: InitiativeGroup[]): InitiativeState[] {
  const seen = new Set<string>();
  const out: InitiativeState[] = [];
  for (const g of groups) {
    for (const i of g.items) {
      if (seen.has(i.initiative_key)) continue;
      seen.add(i.initiative_key);
      out.push(i);
    }
  }
  return out;
}

// ── Blocked-by ───────────────────────────────────────────────────────────────

/** What the blocked-by rules read: a full InitiativeState or the meetings
 * payload's MeetingInitiative both qualify. */
export type Blockable = Pick<InitiativeState, 'initiative_key' | 'title' | 'status' | 'depends_on' | 'external_blocker'>;

export interface BlockedPair<T extends Blockable = InitiativeState> {
  blocked: T;
  /** null = status says blocked but no tracked blocker is named. */
  blocker: T | null;
}

/** Every "X is waiting on Y" the group should see, live blockers only (a done
 * or dropped dependency no longer blocks). `all` resolves the blockers: a
 * filtered-out blocker (a parked one, another team's) still holds X up. */
export function blockedPairs<T extends Blockable>(initiatives: T[], all: T[] = initiatives): BlockedPair<T>[] {
  const byKey = new Map(all.map((i) => [i.initiative_key, i]));
  const pairs: BlockedPair<T>[] = [];
  for (const i of initiatives) {
    // A parked item waiting on something is in the Parked bucket, not here;
    // an active item waiting on a parked one still shows (that is the news).
    if (i.status === 'done' || i.status === 'dropped' || isParked(i)) continue;
    const live = i.depends_on
      .map((k) => byKey.get(k))
      .filter((d): d is T => !!d && d.status !== 'done' && d.status !== 'dropped');
    for (const d of live) pairs.push({ blocked: i, blocker: d });
    if (live.length === 0 && i.status === 'blocked') pairs.push({ blocked: i, blocker: null });
  }
  return pairs;
}

// ── Timeline set (kanban bb978715) ───────────────────────────────────────────

export interface TimelineSet {
  /** What the Timeline draws: the filtered set plus the retained blockers. */
  items: InitiativeState[];
  /** Keys of done initiatives kept only because shown work still lists them. */
  retained: ReadonlySet<string>;
}

/** The Timeline hides done work like every other tab, with one exception: a
 * done initiative that something shown still lists in `depends_on` stays, so
 * the arrow and the "X waits on Y" context survive until Merlin prunes the
 * edge. The exception lifts the done-hide only; a blocker the team / person /
 * text filters exclude stays excluded, exactly as a live one would. */
export function timelineSet(visible: InitiativeState[], all: InitiativeState[], f: RoadmapFilters, me: RoadmapMe | null = null): TimelineSet {
  if (f.includeClosed) return { items: visible, retained: new Set() };
  const shown = new Set(visible.map((i) => i.initiative_key));
  const named = new Set(visible.flatMap((i) => i.depends_on));
  const kept = applyFilters(all, { ...f, includeClosed: true, status: 'all', health: 'all' }, me).filter(
    (i) => i.status === 'done' && named.has(i.initiative_key) && !shown.has(i.initiative_key),
  );
  return { items: [...visible, ...kept], retained: new Set(kept.map((i) => i.initiative_key)) };
}

// ── Parked bucket ────────────────────────────────────────────────────────────

export interface ParkedItem {
  initiative: InitiativeState;
  /** The stated why, or null when nobody said (Merlin asks next run). */
  reason: string | null;
  /** What it waits on: the outside blocker, then tracked blockers still live. */
  waitingOn: { label: string; key: string | null }[];
}

/** The Parked tab: sidelined or indefinitely blocked initiatives, under the
 * same team / person / text filters as every other tab, most recently moved
 * first. */
export function parkedBucket(all: InitiativeState[], f: RoadmapFilters, me: RoadmapMe | null = null): ParkedItem[] {
  const byKey = new Map(all.map((i) => [i.initiative_key, i]));
  return applyFilters(all, { ...f, status: 'parked', health: 'all' }, me)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at) || a.title.localeCompare(b.title))
    .map((i) => {
      const waitingOn: ParkedItem['waitingOn'] = [];
      if (i.external_blocker) waitingOn.push({ label: i.external_blocker, key: null });
      for (const k of i.depends_on) {
        const d = byKey.get(k);
        if (d && d.status !== 'done' && d.status !== 'dropped') waitingOn.push({ label: d.title, key: k });
      }
      return { initiative: i, reason: i.parked_reason ?? null, waitingOn };
    });
}

// ── History across runs ──────────────────────────────────────────────────────

export interface RunGroup {
  reportId: string | null;
  reportTitle: string;
  reportDate: string | null;
  events: RoadmapEvent[];
}

/** Group events (newest first) by the run that applied them. */
export function groupEventsByRun(events: RoadmapEvent[]): RunGroup[] {
  const groups: RunGroup[] = [];
  const index = new Map<string, RunGroup>();
  for (const e of events) {
    const id = e.report_id ?? '';
    let g = index.get(id);
    if (!g) {
      g = {
        reportId: e.report_id,
        // A human edit on /roadmap has no run behind it (kanban ae374351).
        reportTitle: e.report_title ?? (e.kind === 'edited' ? 'Edited on the roadmap page' : 'Roadmap run'),
        reportDate: e.report_date,
        events: [],
      };
      index.set(id, g);
      groups.push(g);
    }
    g.events.push(e);
  }
  return groups;
}

const FIELD_LABELS: Record<string, string> = {
  status: 'status',
  owner: 'owner',
  target_date: 'target',
  started_on: 'start',
  depends_on: 'blocked by',
  related_to: 'related to',
  teams: 'teams',
  owner_team: 'owning team',
  related_teams: 'related teams',
  people: 'people',
  title: 'title',
  summary: 'summary',
  sources: 'sources',
  open_questions: 'open questions',
  actions: 'actions',
  decisions: 'decisions',
  priority: 'priority',
  external_blocker: 'waiting on',
  parked_reason: 'parked because',
};

function fmtOne(v: unknown): string {
  // An action (contract object) reads as its title, never "[object Object]".
  if (v && typeof v === 'object' && 'title' in v) {
    const a = v as { title: unknown; status?: unknown };
    return a.status && a.status !== 'unknown' ? `${String(a.title)} (${String(a.status).replace('_', ' ')})` : String(a.title);
  }
  // A decision (contract v4 object) reads as what was decided, by whom, when.
  if (v && typeof v === 'object' && 'what' in v) {
    const d = v as { what: unknown; who?: unknown; on?: unknown };
    const who = Array.isArray(d.who) ? d.who.map(String).join(', ') : d.who ? String(d.who) : '';
    const tail = [who, d.on ? String(d.on) : ''].filter(Boolean).join(', ');
    return tail ? `${String(d.what)} (${tail})` : String(d.what);
  }
  if (v && typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.map(fmtOne).join(', ');
  return fmtOne(v);
}

/** At most this many items are spelled out per added / removed line. */
const LIST_CHANGE_CAP = 3;

function capped(items: string[]): string {
  const head = items.slice(0, LIST_CHANGE_CAP).join('; ');
  return items.length > LIST_CHANGE_CAP ? `${head}; and ${items.length - LIST_CHANGE_CAP} more` : head;
}

function actionStatus(v: unknown): string | null {
  if (v && typeof v === 'object' && 'title' in v) {
    const st = (v as { status?: unknown }).status;
    return st && st !== 'unknown' ? String(st).replace('_', ' ') : null;
  }
  return null;
}

function itemKey(v: unknown): string {
  // Actions are the same item across a status move.
  if (v && typeof v === 'object' && 'title' in v) return String((v as { title: unknown }).title);
  // A decision is the same decision when Merlin rewords it: same day, same
  // people. Without this a reworded decision printed twice, as "+" and "−".
  if (v && typeof v === 'object' && 'what' in v) {
    const d = v as { what: unknown; who?: unknown; on?: unknown };
    if (d.on) return `decision:${String(d.on)}:${Array.isArray(d.who) ? d.who.map(String).sort().join(',') : ''}`;
  }
  return fmtOne(v);
}

/** Longest a single listed item runs before it is cut with an ellipsis. */
const ITEM_MAX = 160;
const clip = (s: string) => (s.length > ITEM_MAX ? `${s.slice(0, ITEM_MAX - 1).trimEnd()}…` : s);

/**
 * A list field as what was added, what was removed and which actions moved,
 * never the whole list before and after: a run that ticks one sub-action
 * done used to print all twelve twice (owner review 4jsvwe, "wall of text").
 */
function describeListChange(label: string, from: unknown[], to: unknown[]): string[] {
  const before = new Map(from.map((v) => [itemKey(v), v]));
  const after = new Map(to.map((v) => [itemKey(v), v]));
  const added = to.filter((v) => !before.has(itemKey(v))).map(fmtOne).map(clip);
  const removed = from.filter((v) => !after.has(itemKey(v))).map(fmtOne).map(clip);
  const moved = to.flatMap((v) => {
    const prev = before.get(itemKey(v));
    const was = prev === undefined ? null : actionStatus(prev);
    const now = actionStatus(v);
    return prev !== undefined && was !== now ? [`${itemKey(v)}: ${was ?? 'not started'} → ${now ?? 'not started'}`] : [];
  });
  const lines = [
    ...moved.map((m) => `${label}: ${m}`),
    ...(added.length > 0 ? [`${label}: + ${capped(added)}`] : []),
    ...(removed.length > 0 ? [`${label}: − ${capped(removed)}`] : []),
  ];
  return lines.length > 0 ? lines : [`${label}: reordered`];
}

/**
 * Human-readable lines for one recorded change ("status: proposed → in
 * progress"). Scalar fields read before → after; list fields read as what
 * was added / removed; open questions read as a count, because Merlin
 * rewords them every run and the full text is on the questions tab.
 */
export function describeChanges(ev: RoadmapEvent): string[] {
  if (ev.kind === 'created') return ['first tracked'];
  const lines = Object.entries(ev.changes)
    // Summaries/sources churn is noise next to status/date/owner movement.
    .filter(([field]) => field !== 'summary' && field !== 'sources')
    .flatMap(([field, c]) => {
      const label = FIELD_LABELS[field] ?? field;
      const isList = Array.isArray(c.from) || Array.isArray(c.to);
      if (!isList) {
        // A status reads in words ("in progress"), never as its slug.
        const word = (v: unknown) =>
          field === 'status' && typeof v === 'string' && v in STATUS_LABELS
            ? STATUS_LABELS[v as InitiativeStatus].toLowerCase()
            : fmt(v);
        return [`${label}: ${word(c.from)} → ${word(c.to)}`];
      }
      const from = Array.isArray(c.from) ? c.from : [];
      const to = Array.isArray(c.to) ? c.to : [];
      if (field === 'open_questions') {
        return [from.length === to.length ? `${label}: reworded (${to.length})` : `${label}: ${from.length} → ${to.length}`];
      }
      return describeListChange(label, from, to);
    });
  return lines.length > 0 ? lines : ['details refreshed'];
}

/**
 * Merlin's questions under the page's Team / Person / Find filters, so the
 * questions tab narrows with every other tab (it used to ignore them). A
 * team keeps the questions on that team's initiatives; a person keeps the
 * ones addressed to them ("Unassigned" keeps the ones that name nobody, the
 * tab's own heading for them); Find matches the question, its initiative
 * title, people or teams, as the list's Find does. Status / health / closed
 * describe initiatives, not asks, and never reach the questions.
 */
export function filterQuestions<Q extends { teams: string[]; people: string[]; addressed_to?: string[]; question: string; title: string }>(
  items: Q[],
  filters: Pick<RoadmapFilters, 'teams' | 'person' | 'text' | 'mine'>,
  me: RoadmapMe | null = null,
): Q[] {
  const teams = new Set(filters.teams.map((t) => t.toLowerCase()));
  const person = filters.person?.toLowerCase() ?? null;
  const text = filters.text.trim().toLowerCase();
  // Mine: asked of me, or on an initiative that is mine (same rule as the list).
  const mine = filters.mine ? me ?? { names: [], teams: [] } : null;
  return items.filter((q) => {
    if (mine && !questionIsMine(q, mine)) return false;
    if (teams.size > 0 && !q.teams.some((t) => teams.has(t.toLowerCase()))) return false;
    if (person !== null) {
      const addressees = q.addressed_to && q.addressed_to.length > 0 ? q.addressed_to : q.people;
      const has = addressees.some((p) => p.toLowerCase() === person);
      if (person === UNASSIGNED.toLowerCase() ? addressees.length > 0 : !has) return false;
    }
    if (text && ![q.question, q.title, ...q.people, ...q.teams].join(' ').toLowerCase().includes(text)) return false;
    return true;
  });
}

/**
 * The whole questions payload as the tab shows it under the filter bar.
 * Per-initiative asks go through filterQuestions. Group-level asks and the
 * unplaced notes belong to no initiative, so they carry no team or person: a
 * Team or Person filter hides them (a narrowed tab reads as "this team's /
 * this person's asks", and the tab badge counts the same set), while Find
 * still searches their text. The page tells the reader how many it hid
 * (hiddenQuestionCounts), so a narrowed view never loses them silently.
 */
export function filterQuestionsResponse(
  data: ClarifyingQuestionsResponse | null,
  filters: Pick<RoadmapFilters, 'teams' | 'person' | 'text' | 'mine'>,
  me: RoadmapMe | null = null,
): ClarifyingQuestionsResponse | null {
  if (!data) return null;
  // A group ask belongs to nobody in particular, so Mine hides it like a Team or Person filter does.
  const scoped = filters.teams.length > 0 || filters.person !== null || filters.mine;
  const text = filters.text.trim().toLowerCase();
  const keep = (g: GroupQuestion) => !scoped && (!text || g.question.toLowerCase().includes(text));
  return {
    ...data,
    items: filterQuestions(data.items, filters, me),
    group_questions: data.group_questions.filter(keep),
    unplaced: data.unplaced.filter(keep),
  };
}

/** Everything the tab could list: per-initiative asks, group asks, unplaced notes. */
export function questionCount(data: ClarifyingQuestionsResponse | null): number {
  return data ? data.items.length + data.group_questions.length + data.unplaced.length : 0;
}

/** How many group asks and unplaced notes the Team / Person filter took off
 * the tab (the per-initiative asks the filter drops are what the reader asked
 * to narrow away; these two kinds have no team to match, so they are named). */
export function hiddenQuestionCounts(
  all: ClarifyingQuestionsResponse | null,
  shown: ClarifyingQuestionsResponse | null,
): { group: number; unplaced: number } {
  if (!all || !shown) return { group: 0, unplaced: 0 };
  return {
    group: all.group_questions.length - shown.group_questions.length,
    unplaced: all.unplaced.length - shown.unplaced.length,
  };
}

/** Who answers for the initiative: the named owner, else the people holding
 * its live sub-actions (it used to say "no named owner" over a list of
 * sub-actions all held by one person). */
export function ownerLine(i: InitiativeState): string {
  if (i.owner) return `owner ${i.owner}`;
  const holders = [...new Set(liveActions(i.actions).flatMap((a) => a.people))];
  return holders.length > 0 ? `no named owner · sub-actions with ${holders.join(', ')}` : 'no named owner';
}


/** The initiative's teams as a colour bar on the row's left edge: one band
 * per team, painted as a 4px background strip (a border-image is ignored on
 * collapsed table cells, where it drew black). */
export function teamBarStyle(teams: string[], colors: TeamColors): CSSProperties {
  const cs = teams.length > 0 ? teams.map((t) => colors[t] ?? NO_TEAM_COLOR) : [NO_TEAM_COLOR];
  const step = 100 / cs.length;
  // Two stops per band (the long-standing gradient syntax), so every band has hard edges.
  const stops = cs.map((c, n) => `${c} ${n * step}%, ${c} ${(n + 1) * step}%`).join(', ');
  return {
    backgroundImage: `linear-gradient(to bottom, ${stops})`,
    backgroundSize: '4px 100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left top',
  };
}


/**
 * The sub-actions one event added or moved (status changed), by key, so the
 * history can open each one. Events written before actions carried keys
 * (contract v3 derives one) yield nothing rather than a guess.
 */
export function changedActionRefs(ev: RoadmapEvent): ActionRef[] {
  // A "first tracked" event names no sub-action in its line, so it links none
  // (its whole list would pad the history, not link a mention).
  if (ev.kind === 'created') return [];
  const c = ev.changes.actions;
  if (!c) return [];
  const to = Array.isArray(c.to) ? c.to : [];
  const from = Array.isArray(c.from) ? c.from : [];
  const before = new Map(from.map((v) => [itemKey(v), v]));
  const out: ActionRef[] = [];
  const seen = new Set<string>();
  for (const v of to) {
    if (!v || typeof v !== 'object' || !('key' in v) || !('title' in v)) continue;
    const key = String((v as { key: unknown }).key);
    const prev = before.get(itemKey(v));
    const changed = prev === undefined || actionStatus(prev) !== actionStatus(v);
    if (!changed || !key || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, title: String((v as { title: unknown }).title) });
  }
  return out;
}

/** Lines that record churn, not change: Merlin rewords and reorders every run. */
const NOISE = /: (reordered|reworded \(\d+\))$|^details refreshed$|^related to: /;

/** What is worth reading in one event; empty when the run only shuffled words. */
export function meaningfulChanges(ev: RoadmapEvent): string[] {
  return describeChanges(ev).filter((line) => !NOISE.test(line));
}


export interface WaitingLine<T extends Blockable = InitiativeState> {
  initiative: T;
  /** What it waits on, in order: live tracked blockers, then the outside blocker. */
  on: { title: string; key: string | null }[];
}

/**
 * Every active initiative that is held up, once each, with all it waits on:
 * a live tracked dependency, an outside blocker, or (status blocked with
 * nothing named) nobody having said what. The meeting view, the Blocked view
 * and the Blocked count all read this, so they cannot disagree (they showed
 * 6, 5 and "0 stuck" for the same roadmap).
 */
export function waitingLines<T extends Blockable>(initiatives: T[], all: T[] = initiatives): WaitingLine<T>[] {
  const byKey = new Map<string, WaitingLine<T>>();
  const line = (i: T) => {
    const l = byKey.get(i.initiative_key) ?? { initiative: i, on: [] };
    byKey.set(i.initiative_key, l);
    return l;
  };
  for (const pair of blockedPairs(initiatives, all)) {
    if (pair.blocker) line(pair.blocked).on.push({ title: pair.blocker.title, key: pair.blocker.initiative_key });
    else if (!pair.blocked.external_blocker) line(pair.blocked).on.push({ title: 'nobody has said what', key: null });
  }
  for (const i of initiatives) {
    if (i.status === 'done' || i.status === 'dropped' || isParked(i) || !i.external_blocker) continue;
    line(i).on.push({ title: i.external_blocker, key: null });
  }
  return [...byKey.values()];
}
